import React from "react";
import { cn } from "@/utils/utils";
import {
  GATE_DEMOTE_BELOW,
  GATE_PROMOTE_AT,
  headlineScore,
} from "../../utils/diagnosticScoring";
import { stageColor } from "../../utils/stageColors";
import { stageLabel } from "../../utils/stages";
import type {
  DiagnosticFinalOutcome,
  DiagnosticResultEnvelope,
} from "../../types/diagnostic";

const RING_RADIUS = 19;
const RING = 2 * Math.PI * RING_RADIUS;

/**
 * Coloured by position on the ladder, so Build and Scale are visibly different
 * stages. This is the one place in the feature that uses colour to carry
 * meaning: the placement is the result, not a status decoration.
 */
const StagePill: React.FC<{
  label: string;
  size?: "sm" | "lg";
  stage: number | null;
}> = ({ label, size = "sm", stage }) => (
  <span
    className={cn(
      "inline-block rounded-full text-white",
      size === "lg"
        ? "px-6 py-2 text-[15px] tracking-wide"
        : "px-2 py-0.5 text-[11px]",
    )}
    style={{ backgroundColor: stageColor(stage) }}
  >
    {label}
  </span>
);

const RailRow: React.FC<{ children: React.ReactNode; label: string }> = ({
  children,
  label,
}) => (
  <div className="mb-2.5 flex items-center gap-2.5 text-[13px] last:mb-0">
    <span className="flex-1 text-gray-500 dark:text-gray-400">{label}</span>
    {children}
  </div>
);

const Panel: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={cn(
      "rounded-lg border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800",
      className,
    )}
  >
    {children}
  </div>
);

export interface ResultRailProps {
  answeredCount: number;
  bandLabels: string[];
  /** False while any applicable gate question is still unanswered. */
  gateAnswered: boolean;
  /**
   * Sections that set the stage ceiling rather than contributing to the score.
   *
   * The result envelope's `perSection` rows carry `isScored` but not `isGate`,
   * so the breakdown below cannot tell one from the other without being told.
   * `diagnosticReportData` reads `isGate` off the version tree for the same
   * filter; this is the rail's half of it.
   */
  gateSectionIds?: number[];
  /**
   * True when the instrument asks trade-specific questions and the coach has
   * chosen no trade yet.
   *
   * Load-bearing, not decoration: with nothing selected the visible instrument
   * is only the untagged questions, so answering them makes `answeredCount`
   * equal `totalQuestions` while the server would still refuse the completion.
   * Mirrors `DiagnosticAttemptService::complete()`, which refuses a version
   * with trade-tagged questions and no trade selected.
   */
  needsTrades?: boolean;
  preview: DiagnosticResultEnvelope | null;
  /**
   * The outcome recorded when the attempt was completed. See
   * `recordedStageLabel` — read the same way, for the same reason.
   */
  recordedOutcome?: DiagnosticFinalOutcome | null;
  /**
   * The stage recorded when the attempt was completed, preferred over the
   * envelope's recomputed placement whenever it is supplied.
   *
   * The rail is a *live* view: while a coach is answering there is nothing
   * recorded and the recomputation is the only truth available, and D13
   * deliberately lets a mid-correction screen lead the artifact the owner
   * already holds. A finished attempt being read back is the other case — it
   * has a record, and the rail renders inches below the badge that shows it.
   * Deriving here let one screen print two different stages for one
   * diagnostic, which is the same defect BS3 fixed in the PDF.
   *
   * Absent (or null) means "derive it" — a draft, or a correction in progress.
   */
  recordedStageLabel?: string | null;
  /**
   * Highest score any authored choice can award. Labs hardcoded 3 and divided
   * the score bars by it unclamped, so an instrument authored on a wider scale
   * overflowed the track.
   */
  scoreCeiling: number;
  totalQuestions: number;
  /** Trade names being assessed, shown so the placement carries its scope. */
  tradeNames?: string[];
}

/**
 * Live placement rail: how far through the coach is, where the company lands,
 * and why.
 *
 * The placement has to be readable while answering rather than only at the
 * end — a coach uses it to sanity-check the picture as it forms.
 */
export const ResultRail: React.FC<ResultRailProps> = ({
  answeredCount,
  bandLabels,
  gateAnswered,
  gateSectionIds = [],
  needsTrades = false,
  preview,
  recordedOutcome = null,
  recordedStageLabel = null,
  scoreCeiling,
  totalQuestions,
  tradeNames = [],
}) => {
  const progress = totalQuestions > 0 ? answeredCount / totalQuestions : 0;
  const percent = Math.round(progress * 100);
  const gate = preview?.gate ?? null;
  // `!needsTrades` is the half that is easy to lose: with no trade chosen the
  // visible instrument is only the untagged questions, so every one of them
  // could be answered while the server still refuses to complete the attempt.
  const complete =
    !needsTrades && totalQuestions > 0 && answeredCount === totalQuestions;

  /**
   * The gate reads by dimension — org structure, revenue — not by question.
   * With four trades selected there are five gate answers but still only two
   * dimensions, and it is the dimensions that decide the ceiling.
   */
  const gateRows =
    gate && gate.groups.length > 0
      ? gate.groups.map((g) => ({ key: g.key, label: g.label, stage: g.stage }))
      : (gate?.questions ?? []).map((q, i) => ({
          key: String(q.questionId),
          label: `Q${i + 1}`,
          stage: q.stage,
        }));

  /**
   * The sections the score bars are drawn for.
   *
   * Gate sections are excluded as well as unscored ones. A gate section
   * carries no weight and contributes nothing to the number the bars are
   * scaled against, so it can only ever draw an empty 0% bar beside the
   * sections that do — which is why `diagnosticReportData` has filtered it out
   * of the PDF's breakdown, with the reason written beside it, since it was
   * written. Filtering `isScored` alone left the rail and the PDF giving two
   * different accounts of one result.
   */
  const gateSections = new Set(gateSectionIds);
  const breakdown = (preview?.perSection ?? []).filter(
    (ps) => ps.isScored && !gateSections.has(ps.sectionId),
  );

  /** Revenue answers that were averaged rather than taken one at a time. */
  const averagedRevenueCount = (gate?.questions ?? []).filter(
    (q) => q.gateGroup === "revenue",
  ).length;

  const progressNote = (() => {
    // Ahead of the answer count, which is counting the wrong instrument until
    // a trade is chosen — "0 left before a stage is placed" was the reading a
    // coach got on a diagnostic that could not be completed at all.
    if (needsTrades) return "Select a trade before a stage can be placed.";
    // Only where there *are* gate questions. An ungated instrument told the
    // coach to start with questions it does not contain — a rail describing
    // some other diagnostic. Falling through gives it the count instead, which
    // is the line it would have shown on its second answer anyway.
    if (gate && answeredCount === 0) return "Start with the gate questions.";
    if (!gateAnswered) {
      return "Gate questions still open — those set the ceiling.";
    }
    if (!complete) {
      return `${totalQuestions - answeredCount} left before a stage is placed.`;
    }
    return "Complete.";
  })();

  /**
   * The headline number: a gated diagnostic's secondary score, an ungated
   * one's weighted total.
   *
   * Null means *nothing has been scored yet*, which D15 made the engines
   * distinguish from a genuine `0`. `preview.weightedTotal` is
   * `secondaryScore ?? 0` and so collapses the two, which printed a 44px
   * **0.00** for a coach who had answered only the gate — directly above prose
   * in this same component reading **—**. `headlineScore()` keeps the null on
   * both paths, and the PDF's `secondaryScore` reads the same helper.
   */
  const headline = preview ? headlineScore(preview, gateSectionIds) : null;

  const derivedLabel = gate
    ? stageLabel(gate.finalStage, bandLabels)
    : (preview?.band?.label ?? "—");
  // The record wins outright where there is one; the derivation is the
  // fallback, not the other way round.
  const finalLabel = recordedStageLabel ?? derivedLabel;
  // The pill's colour follows the word printed on it. A recorded label the
  // ladder no longer defines stays uncoloured rather than borrowing the
  // recomputed placement's position, which would let "Grow" wear Lead's
  // colour — the same rule `diagnosticReportData` applies to the PDF's pill.
  const finalStageIndex =
    recordedStageLabel != null
      ? bandLabels.indexOf(recordedStageLabel)
      : gate
        ? gate.finalStage
        : bandLabels.indexOf(finalLabel);
  const printedOutcome = recordedOutcome ?? preview?.finalOutcome ?? null;

  // Plain-English account of how the placement was reached, so a coach can
  // explain the result rather than just report it. The thresholds are read
  // from the scoring constants, not spelled out, so the copy cannot drift
  // from the arithmetic.
  const reasons: string[] = [];
  if (gate && gate.gateStage !== null) {
    reasons.push(
      `Gate answers set the ceiling at ${stageLabel(
        gate.gateStage,
        bandLabels,
      )} — the lowest of ${gateRows.length} decides.`,
    );
    if (averagedRevenueCount > 1) {
      reasons.push(
        `Revenue is the average of ${averagedRevenueCount} trades, so the smallest one does not cap the company.`,
      );
    }
    const score = gate.secondaryScore?.toFixed(2) ?? "—";
    if (gate.adjustment === "up") {
      reasons.push(
        `Secondary score ${score} is ${GATE_PROMOTE_AT.toFixed(2)} or better, promoting one stage.`,
      );
    } else if (gate.adjustment === "down") {
      reasons.push(
        `Secondary score ${score} is below ${GATE_DEMOTE_BELOW.toFixed(2)}, dropping one stage.`,
      );
    } else {
      reasons.push(`Secondary score ${score} holds the placement at the gate.`);
    }
  }

  return (
    <div className="flex flex-col gap-4 lg:sticky lg:top-16">
      <Panel className="flex items-center gap-3.5 p-4">
        <div className="relative h-11 w-11 flex-shrink-0">
          <svg
            aria-valuemax={100}
            aria-valuemin={0}
            aria-valuenow={percent}
            aria-valuetext={`${answeredCount} of ${totalQuestions} answered`}
            className="-rotate-90"
            height="44"
            role="progressbar"
            viewBox="0 0 44 44"
            width="44"
          >
            <title>Diagnostic progress</title>
            {/* Unfilled track. A fixed light grey vanished against the dark
                page, leaving the ring with no visible remainder to read. */}
            <circle
              className="stroke-gray-200 dark:stroke-slate-700"
              cx="22"
              cy="22"
              fill="none"
              r={RING_RADIUS}
              strokeWidth="3.5"
            />
            <circle
              className="stroke-gray-900 dark:stroke-slate-300"
              cx="22"
              cy="22"
              fill="none"
              r={RING_RADIUS}
              strokeDasharray={RING}
              strokeDashoffset={RING * (1 - progress)}
              strokeLinecap="round"
              strokeWidth="3.5"
              style={{ transition: "stroke-dashoffset .35s ease" }}
            />
          </svg>
          <span className="absolute inset-0 grid place-items-center text-[11px] tabular-nums text-gray-900 dark:text-white">
            {percent}%
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-gray-900 dark:text-white">
            {`${answeredCount} of ${totalQuestions} answered`}
          </div>
          <div className="mt-0.5 text-[13px] text-gray-500 dark:text-gray-400">
            {progressNote}
          </div>
          {tradeNames.length > 0 && (
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Assessing {tradeNames.join(", ")}
            </div>
          )}
        </div>
      </Panel>

      <Panel className="overflow-hidden">
        <div
          className="h-[3px]"
          // The placement the panel prints, so the strip cannot show one stage
          // while the pill below it shows another — it read `gate.finalStage`,
          // which is permanently grey on an ungated instrument the pill
          // colours, and ignores a recorded stage entirely.
          style={{ backgroundColor: stageColor(finalStageIndex) }}
        />
        <div className="p-5 text-center">
          {gate && (
            <>
              <div className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                Gate — lowest answer sets the ceiling
              </div>
              <div className="mb-5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1.5">
                {gateRows.map((row) => (
                  <React.Fragment key={row.key}>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {row.label}
                    </span>
                    <StagePill
                      label={stageLabel(row.stage, bandLabels)}
                      stage={row.stage}
                    />
                  </React.Fragment>
                ))}
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Gate
                </span>
                <StagePill
                  label={stageLabel(gate.gateStage, bandLabels)}
                  stage={gate.gateStage}
                />
              </div>
            </>
          )}

          <div
            className="text-[44px] font-extralight leading-none tabular-nums tracking-tight text-gray-900 dark:text-white"
            data-testid="rail-headline-score"
          >
            {headline == null ? "—" : headline.toFixed(2)}
          </div>
          <div className="mb-5 mt-1 text-xs text-gray-500 dark:text-gray-400">
            {gate
              ? // Offering a scale for a score that does not exist reads as a
                // broken number rather than an empty one, so the caption says
                // what the dash means — the PDF's wording for the same state.
                headline == null
                ? "Secondary score — nothing scored yet"
                : `Secondary score — out of ${scoreCeiling.toFixed(2)}`
              : "Weighted total"}
          </div>

          <StagePill
            label={finalLabel === "—" ? "Incomplete" : finalLabel}
            size="lg"
            stage={finalStageIndex}
          />
          <p className="mx-auto mt-2.5 max-w-[17rem] text-[13px] leading-relaxed text-gray-500 dark:text-gray-400">
            {complete || recordedOutcome != null
              ? printedOutcome === "ready"
                ? "Ready to advance."
                : "Not yet ready to advance."
              : "Answer every question to place a stage."}
          </p>
        </div>

        <div className="border-t border-gray-200 px-5 py-4 text-left dark:border-slate-700">
          <div className="mb-2 text-xs text-gray-500 dark:text-gray-400">
            Determination
          </div>
          <ul className="list-disc pl-4 text-[13px] leading-relaxed text-gray-500 marker:text-gray-400 dark:text-gray-400 dark:marker:text-slate-500">
            {(reasons.length > 0
              ? reasons
              : ["Answer the gate questions to place a stage."]
            ).map((reason) => (
              <li className="mb-1" key={reason}>
                {reason}
              </li>
            ))}
          </ul>
        </div>
      </Panel>

      {gate && (
        <Panel className="p-4">
          <div className="mb-3.5 border-b border-gray-200 pb-3 text-[13px] text-gray-900 dark:border-slate-700 dark:text-white">
            Gate logic
          </div>
          <RailRow label="Gate ceiling">
            <StagePill
              label={stageLabel(gate.gateStage, bandLabels)}
              stage={gate.gateStage}
            />
          </RailRow>
          <RailRow label="Adjustment">
            <span className="text-xs tabular-nums text-gray-900 dark:text-white">
              {gate.adjustment === "up"
                ? "+1 stage"
                : gate.adjustment === "down"
                  ? "-1 stage"
                  : "No change"}
            </span>
          </RailRow>
          <RailRow label="Final stage">
            <StagePill label={finalLabel} stage={finalStageIndex} />
          </RailRow>
        </Panel>
      )}

      {breakdown.length > 0 && (
        <Panel className="p-4">
          <div className="mb-3.5 border-b border-gray-200 pb-3 text-[13px] text-gray-900 dark:border-slate-700 dark:text-white">
            Score breakdown
          </div>
          {breakdown.map((ps) => (
            <div
              className="mb-3 flex items-center gap-2.5 text-[13px] last:mb-0"
              key={ps.sectionId}
            >
              <span className="flex-[1.3] text-gray-500 dark:text-gray-400">
                {ps.name}
              </span>
              <span className="h-1.5 flex-[1.2] overflow-hidden rounded-full bg-gray-200 dark:bg-slate-700">
                <span
                  className="block h-full rounded-full bg-gray-900 transition-[width] duration-300 dark:bg-slate-300"
                  style={{
                    // Clamped: an authored scale that runs past the ceiling
                    // would otherwise overflow the track.
                    width: `${Math.min(100, Math.max(0, ((ps.rawAvg ?? 0) / scoreCeiling) * 100))}%`,
                  }}
                />
              </span>
              <span className="w-9 flex-shrink-0 text-right text-xs tabular-nums text-gray-900 dark:text-white">
                {ps.rawAvg == null ? "—" : ps.rawAvg.toFixed(2)}
              </span>
            </div>
          ))}
        </Panel>
      )}
    </div>
  );
};

export default ResultRail;
